class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    // Internal state — source of truth. We do NOT rely on the details[open]
    // attribute as the authority because Safari fires its own native toggle
    // AFTER preventDefault(), silently flipping the attribute back.
    this._isOpen = false;
    this._closeTimer = null;
    this._bodyListenerActive = false;

    // Bind once so add/removeEventListener always get the same reference
    this._onBodyClick = this._handleBodyClick.bind(this);

    // ── Safari desktop fix ───────────────────────────────────────────────
    // Safari does NOT honour preventDefault() on <summary> click — it still
    // fires its native <details> toggle AFTER our handler runs, which flips
    // the [open] attribute back and collapses our modal a frame later.
    // We neutralise this by listening to the 'toggle' event and immediately
    // re-syncing the attribute to match our _isOpen state.
    this.detailsContainer.addEventListener('toggle', () => {
      const nativeSaysOpen = this.detailsContainer.hasAttribute('open');
      if (nativeSaysOpen !== this._isOpen) {
        // Safari sneaked a toggle in — put it back
        if (this._isOpen) {
          this.detailsContainer.setAttribute('open', '');
        } else {
          this.detailsContainer.removeAttribute('open');
        }
      }
    });

    // ── Summary activation (one handler for both desktop + mobile) ────────
    // Previous implementation split activation across pointerup (for activation)
    // and click (for preventDefault). On iOS Safari, pointerup is unreliable
    // when the tap lands on a child SVG/<use> inside <summary>, so the first
    // tap would only fire 'click' (which just preventDefault'd) and the modal
    // wouldn't open until a second tap. This single-handler approach uses
    // 'click' as the canonical activation event — it fires reliably on every
    // platform — and preventDefault stops the native <details> toggle. The
    // 'toggle' event listener above is the safety net that re-syncs the
    // [open] attribute if a browser ignores preventDefault.
    this.summaryToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this._onSummaryActivated();
    });

    // Escape key
    this.detailsContainer.addEventListener('keyup', (e) => {
      if (e.code.toUpperCase() === 'ESCAPE') this.close();
    });

    // Close button
    const closeBtn = this.querySelector('button[type="button"]');
    if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this._isOpen;
  }

  _onSummaryActivated() {
    // If mid-fade, cancel and restore
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
      const modal = this.detailsContainer.querySelector('.search-modal');
      if (modal) modal.classList.add('ffk-search-visible');
      return;
    }
    this._isOpen ? this.close() : this.open();
  }

  _handleBodyClick(event) {
    // Ignore clicks on the toggle itself — already handled by pointerup
    if (this.summaryToggle.contains(event.target)) return;
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
      this.close(false);
    }
  }

  open() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
      const modal = this.detailsContainer.querySelector('.search-modal');
      if (modal) modal.classList.add('ffk-search-visible');
      return;
    }

    this._isOpen = true;
    this.detailsContainer.setAttribute('open', '');

    if (!this.detailsContainer.querySelector('.search-modal')) {
      document.body.classList.add('overflow-hidden');
    }

    if (!this._bodyListenerActive) {
      // Defer installing the body click listener until AFTER the current
      // event loop tick. Otherwise the same click that activated the
      // <summary> bubbles up to body and is processed by _handleBodyClick
      // on the very same tap. _handleBodyClick already bails when the
      // target is inside summary, but iOS sometimes resolves the target to
      // an unexpected element when SVG <use> is involved — deferring is the
      // belt-and-suspenders fix.
      this._bodyListenerActive = true;
      setTimeout(() => {
        if (this._bodyListenerActive) {
          document.body.addEventListener('click', this._onBodyClick);
        }
      }, 0);
    }

    const modal = this.detailsContainer.querySelector('.search-modal');
    if (modal) {
      // iOS Safari fix: focus must fire AFTER the modal is visually shown.
      // Calling input.focus() on a hidden/transitioning element silently fails
      // on Safari iOS — the keyboard never activates and the 'input' event chain
      // never starts, so suggestions never appear.
      // Moving both the class-add AND trapFocus into the same rAF tick guarantees
      // the element is painted and interactive before we focus it.
      requestAnimationFrame(() => {
        modal.classList.add('ffk-search-visible');
        trapFocus(
          this.detailsContainer.querySelector('[tabindex="-1"]'),
          this.detailsContainer.querySelector('input:not([type="hidden"])')
        );
      });
    } else {
      trapFocus(
        this.detailsContainer.querySelector('[tabindex="-1"]'),
        this.detailsContainer.querySelector('input:not([type="hidden"])')
      );
    }
  }

  close(focusToggle = true) {
    const modal = this.detailsContainer.querySelector('.search-modal');

    if (modal) {
      if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
      modal.classList.remove('ffk-search-visible');
      this._closeTimer = setTimeout(() => {
        this._closeTimer = null;
        this._doClose();
      }, 420);
    } else {
      this._doClose();
    }
  }

  _doClose() {
    this._isOpen = false;
    removeTrapFocus(null);
    this.detailsContainer.removeAttribute('open');

    if (this._bodyListenerActive) {
      document.body.removeEventListener('click', this._onBodyClick);
      this._bodyListenerActive = false;
    }

    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
